import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ButtonLink,
  cn,
  Pill,
  RiArrowRightLine,
  RiCheckLine,
  RiFolderLine,
  RiLinksLine,
  RiLockLine,
  Stack,
} from 'component-library';
import { Progress } from 'component-library/Progress/Progress';
import { useTranslation } from 'react-i18next';

import { RoadmapNode, RoadmapNodeStatus } from '@/api/rest/resources/types/organization';
import { Workstream, WORKSTREAM_MILESTONE_STATUS } from '@/api/rest/resources/types/workstreams';
import { useWorkstreamTargetUrl } from '@/pages/shared/home/hooks/useWorkstreamTargetUrl';
import {
  getRoadmapFromWorkstream,
  getWorkstreamMilestones,
  getWorkstreamMilestoneStatus,
  getWorkstreamProgress,
} from '@/pages/shared/home/utils/index';

type WorkstreamCardProps = {
  workstream: Workstream;
};

export const WorkstreamCard = ({ workstream }: WorkstreamCardProps) => {
  const roadmap = getRoadmapFromWorkstream(workstream);

  if (!roadmap) {
    return (
      <Stack className='w-full border border-divider rounded-lg px-6 py-5'>
        <WorkstreamCardPreview workstream={workstream} />
      </Stack>
    );
  }

  return (
    <Accordion type='single' collapsible>
      <AccordionItem value={workstream.id} className='w-full border border-divider rounded-lg'>
        <AccordionTrigger className='px-6 py-5'>
          <WorkstreamCardPreview workstream={workstream} />
        </AccordionTrigger>
        <AccordionContent>
          <WorkstreamCardContent workstream={workstream} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const WorkstreamCardPreview = ({ workstream }: WorkstreamCardProps) => {
  const { t } = useTranslation();

  const { completed, total } = getWorkstreamMilestones(workstream);
  const progress = getWorkstreamProgress(workstream);

  const targetUrl = useWorkstreamTargetUrl(workstream);

  return (
    <Stack direction='row' spacing={4} className='w-full' data-testid='workstream-card'>
      <Stack direction='row' spacing={4} className='items-center grow'>
        <RiFolderLine className='text-primary-100 text-3xl' />
        <Stack direction='col' spacing={1} className='items-start'>
          {workstream.description && (
            <span className='typography-overline text-text-secondary truncate'>{workstream.description}</span>
          )}
          <span className='typography-display5 text-text-primary'>{workstream.title}</span>
        </Stack>
        <ButtonLink
          variant='text'
          className='text-neutral-black-60 p-2 hover:bg-neutral-black-4 rounded-full'
          to={targetUrl}
        >
          <RiArrowRightLine />
        </ButtonLink>
      </Stack>

      <Stack direction='row' spacing={4} className='w-1/5 items-center mx-2'>
        <Stack spacing={1} className='w-full items-start'>
          <Stack direction='row' className='w-full justify-between'>
            <span className='typography-h3 text-primary-100'>{progress}%</span>
            <span className='typography-caption text-text-secondary content-end'>
              {total !== 0 && `${completed}/${total} ${t('global.workstream.labels.milestones')}`}
            </span>
          </Stack>
          <Progress value={progress} className='w-full bg-neutral-black-12' />
        </Stack>
      </Stack>
    </Stack>
  );
};

const WorkstreamCardContent = ({ workstream }: WorkstreamCardProps) => {
  const { t } = useTranslation();
  const roadmap = getRoadmapFromWorkstream(workstream);
  const milestones = roadmap?.children ?? [];
  const targetUrl = useWorkstreamTargetUrl(workstream);

  return (
    <Stack spacing={6}>
      <Stack>
        {milestones.map((milestone, index) => (
          <MilestoneStep
            key={milestone.id ?? index}
            milestone={milestone}
            index={index}
            isLast={index === milestones.length - 1}
            previousMilestone={index > 0 ? milestones[index - 1] : undefined}
          />
        ))}
      </Stack>
      <ButtonLink className='w-fit' rightAdornment={<RiArrowRightLine />} to={targetUrl}>
        {t('global.workstream.labels.openWorkstream')}
      </ButtonLink>
    </Stack>
  );
};

const MilestoneStatusBadge = ({ status }: { status: RoadmapNodeStatus }) => {
  const { t } = useTranslation();
  const milestoneStatus = getWorkstreamMilestoneStatus(status);

  if (milestoneStatus === WORKSTREAM_MILESTONE_STATUS.COMPLETE) {
    return (
      <Pill size='small' className='bg-info-light' leftAdornment={<RiCheckLine className='text-primary-100' />}>
        <span className='typography-overline text-primary-100 uppercase'>{t('global.workstream.status.complete')}</span>
      </Pill>
    );
  }

  if (milestoneStatus === WORKSTREAM_MILESTONE_STATUS.IN_PROGRESS) {
    return (
      <Pill size='small' className='bg-warning-light' leftAdornment={<span className='text-warning-dark'>↻</span>}>
        <span className='typography-overline text-warning-dark uppercase'>
          {t('global.workstream.status.inProgress')}
        </span>
      </Pill>
    );
  }

  return (
    <Pill size='small' className='bg-neutral-black-12' leftAdornment={<RiLockLine className='text-text-secondary' />}>
      <span className='typography-overline text-text-secondary uppercase'>{t('global.workstream.status.locked')}</span>
    </Pill>
  );
};

type MilestoneStepProps = {
  milestone: RoadmapNode;
  index: number;
  isLast: boolean;
  previousMilestone?: RoadmapNode;
};

const MilestoneStep = ({ milestone, index, isLast, previousMilestone }: MilestoneStepProps) => {
  const { t } = useTranslation();
  const { status, title, description } = milestone;
  const milestoneStatus = getWorkstreamMilestoneStatus(status);

  const isCompleted = milestoneStatus === WORKSTREAM_MILESTONE_STATUS.COMPLETE;
  const isInProgress = milestoneStatus === WORKSTREAM_MILESTONE_STATUS.IN_PROGRESS;
  const isLocked = milestoneStatus === WORKSTREAM_MILESTONE_STATUS.LOCKED;

  return (
    <Stack direction='row' spacing={4}>
      <Stack className='items-center'>
        <Stack
          className={cn(
            'h-8 w-8 shrink-0 items-center justify-center rounded-full border-2',
            isCompleted && 'border-primary-light bg-primary-light text-white',
            isInProgress && 'border-primary-light bg-white text-primary-100',
            isLocked && 'border-divider bg-white text-text-secondary',
          )}
        >
          {isCompleted && <RiCheckLine className='text-lg text-white-100' />}
          {isInProgress && <span className='typography-caption font-bold'>{index + 1}</span>}
          {isLocked && <RiLockLine className='text-lg text-text-secondary' />}
        </Stack>
        {!isLast && <div className={cn('w-0.5 grow', isCompleted ? 'bg-primary-light' : 'bg-divider')} />}
      </Stack>
      <Stack spacing={2} className='border border-divider rounded-lg p-4 grow mb-4'>
        <Stack direction='row' className='justify-between items-start'>
          <Stack spacing={1}>
            <span className='typography-h4 text-text-primary'>{title}</span>
            {description && <span className='typography-body2 text-text-secondary'>{description}</span>}
          </Stack>
          <MilestoneStatusBadge status={status} />
        </Stack>
        {previousMilestone && !isCompleted && (
          <>
            <hr className='border-divider border-dashed' />
            <Stack direction='row' spacing={2} className='items-center'>
              <RiLinksLine className='text-text-secondary' />
              <span className='typography-caption text-text-secondary'>
                {isInProgress
                  ? t('global.workstream.labels.unlockedBy', { title: previousMilestone.title })
                  : t('global.workstream.labels.lockedUntil', { title: previousMilestone.title })}
              </span>
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
};
